import { shape, number, string, arrayOf, bool, objectOf, oneOfType, oneOf } from 'prop-types'

const colorType = shape({
    name: string.isRequired,
    hex: string.isRequired,
    material: string.isRequired,
    albedo: string,
})

const ratioType = shape({
    ratio: number.isRequired,
    url: string.isRequired,
})

const matteType = shape({
    id: number.isRequired,
    name: string.isRequired,
    hex: string,
    key: string.isRequired,
})

const frameType = shape({
    id: number.isRequired,
    name: string.isRequired,
    thumbUrl: string.isRequired,
    colors: objectOf(colorType),
    ratios: arrayOf(ratioType),
    color: colorType,
    mattes: objectOf(matteType),
    matte: matteType,
})

const sizeType = shape({
    id: number.isRequired,
    name: string.isRequired,
    height: number.isRequired,
})

// const optionsType = shape({
//     frames: objectOf(frameType).isRequired,
// })

const artProductTypeProps = {
    artAssetUrls: arrayOf(string).isRequired,
    displayWidthCm: number.isRequired,
    displayHeightCm: number.isRequired,
}

const furnitureProductTypeProps = {
    modelUrl: string.isRequired,
    scale: number.isRequired,
    max: shape({
        x: number.isRequired,
        y: number.isRequired,
        z: number.isRequired,
    }).isRequired,
    min: shape({
        x: number.isRequired,
        y: number.isRequired,
        z: number.isRequired,
    }).isRequired,
    isRug: bool.isRequired,
    hasTallLegs: bool.isRequired,
    isCubicStructure: bool.isRequired,
}

const productDataTypeProps = {
    id: number.isRequired,
    region: string.isRequired,
    name: string.isRequired,
    thumbUrl: string.isRequired,

    collections: arrayOf(string).isRequired,
    rootCategories: arrayOf(string),
    categories: arrayOf(string),
    subCategories: arrayOf(string),
    ecommerceUrl: string,
    globalProductId: string,
    price: string,
    rating: string,
    ratingCount: number,
    description: string,
    imageUrls: arrayOf(string),
    qaStatus: string,
    availableInRegion: bool.isRequired,
    uncompressedModelSize: number,
    localItemNumber: string.isRequired,
    recommended: arrayOf(number),
    subCatalogs: arrayOf(string),
    swappableItems: arrayOf(number),
    renderType: string.isRequired,

    globalProductAdminUrl: string,
    localProductAdminUrl: string,
}

const coordinatesProps = {
    x: number.isRequired,
    y: number.isRequired,
}

const compositionDataProps = {
    // TODO: this is missing artItems, furnitureItems
    id: number.isRequired,
    wid: number.isRequired,
    name: string.isRequired,
    thumbUrl: string.isRequired,
    sceneId: number.isRequired,
    creationDate: string,
    isStock: bool.isRequired,
    geoProperties: shape({}).isRequired,
    savedProperties: shape({
        erasedSegments: arrayOf(number),
        lastSavedOn: string,
    }).isRequired,
}

const renderItemProps = {
    ...coordinatesProps,
    uniqueId: number.isRequired,
    selected: bool.isRequired,
    metrics: shape({}),
    readyAndRendered: bool,
    isLeftMost: bool.isRequired,
    combinablePlacement: shape({
        parentId: number,
        localTransform: arrayOf(number),
    }),
}

export const artType = shape(artProductTypeProps)
export const artItemsType = arrayOf(artType)

const dimensionsProps = {
    width: number.isRequired,
    height: number.isRequired,
}
export const dimensionsType = shape(dimensionsProps)

export const sceneType = shape({
    sceneId: number.isRequired,
    jobId: string,
    name: string.isRequired,
    depthUrl: string,
    creationDate: string.isRequired,
    defaultX: number.isRequired,
    defaultY: number.isRequired,
    wid: number.isRequired,
    thumbUrl: string,
    metaData: shape({}),
    manifest: shape({
        pano_0: string.isRequired,
        depth_occlusion_0: string,
        foreground_0: string,
        plane_orientation_0: shape({}).isRequired,
        normals_segmentation_0: string.isRequired,
        lighting: shape({}),
        semantic_segmentation_visual_0: string,
        extra: shape({
            useFloorMask: bool,
        }),
    }).isRequired,
    hasFloor: bool.isRequired,
    hasWall: bool.isRequired,
    hasWallPaint: bool.isRequired,
    supportsShadows: bool.isRequired,
    supportsOcclusion: bool.isRequired,
    supportsForegroundOcclusion: bool.isRequired,
    supportsDepthOcclusion: bool.isRequired,
    isStock: bool.isRequired,
    roomType: string,
})

export const sceneItemsType = arrayOf(sceneType)

export const scenePlaneType = shape({
    planeIdx: number.isRequired,
    class: string.isRequired,
})

export const scenePlanesType = arrayOf(scenePlaneType)

export const rgbColorType = arrayOf(number)
export const wallColorsType = arrayOf(rgbColorType)

export const wallMarkerType = arrayOf(oneOfType([number, string]))
export const wallMarkersType = arrayOf(wallMarkerType)

// TODO add color to frame
export const artItemType = shape({
    ...productDataTypeProps,
    ...artProductTypeProps,
    ...renderItemProps,
    frame: frameType.isRequired,
    frameId: number.isRequired,
    colorId: number.isRequired,
    size: sizeType.isRequired,
    sizeId: number.isRequired,
    customHeight: number,
})

export const furnitureItemType = shape({
    ...productDataTypeProps,
    ...furnitureProductTypeProps,
    ...renderItemProps,
    heading: number.isRequired,
    stackedPlane: string,
})

export const renderItemType = oneOfType([artItemType, furnitureItemType])

export const stackableSurfaceItemType = shape({
    id: string.isRequired,
    transform: arrayOf(arrayOf(number)).isRequired,
    parameters: oneOfType([
        shape({
            type: oneOf(['circle']),
            radius: number.isRequired,
        }),
        shape({
            type: oneOf(['polygon']),
            vertices: arrayOf(arrayOf(number)).isRequired,
        }),
    ]),
})

export const compositionDataType = shape(compositionDataProps)
export const compositionDataItemsType = arrayOf(compositionDataType)

export const compositionType = shape({
    ...compositionDataProps,
    scene: sceneType,
    isMissingItems: bool.isRequired,
    renderItems: arrayOf(renderItemType),
    stackableSurfaces: arrayOf(stackableSurfaceItemType),
    canMagicErase: bool.isRequired,
})

export const userType = shape({
    isLoggedIn: bool.isRequired,
    email: string,
    firstName: string,
    gid: string,
    lastName: string,
    wid: number.isRequired,
    metricsId: string.isRequired,
    isAdmin: bool.isRequired,
    canBeAdmin: bool.isRequired,
    signupLoginStatus: number,
    isNewUser: bool.isRequired,
    loginType: arrayOf(string).isRequired,
    subCatalogs: arrayOf(string),
    redirectUrl: string.isRequired,
    canChangePassword: bool.isRequired,
    passwordResetStatus: string.isRequired,
    changePasswordStatus: string.isRequired,
    triggerPasswordResetStatus: string.isRequired,
    earlyAccessSubscribeStatus: string.isRequired,
})

export const searchOptionsType = shape({
    style: arrayOf(string).isRequired,
})

export const dashboardThumbType = shape({
    thumbUrl: string.isRequired,
    name: string.isRequired,
})

export const furnitureDataType = shape(furnitureProductTypeProps)
export const furnituresDataType = arrayOf(furnitureDataType)
export const productDataType = shape(productDataTypeProps)
export const productsDataType = arrayOf(productDataType)

export const dropdownOptionKeyType = oneOfType([string, number])
export const dropdownOptionType = shape({
    key: dropdownOptionKeyType.isRequired,
    label: string.isRequired,
})
export const dropdownOptionsType = arrayOf(dropdownOptionType)

export const filterType = shape({
    query: string,
    category: string.isRequired,
    subCategory: string.isRequired,
    subCatalog: string.isRequired,
})

export const filterCategoryAndSubCategory = shape({
    byCategory: arrayOf(dropdownOptionType).isRequired,
    bySubCategory: objectOf(dropdownOptionsType).isRequired,
})

export const categoriesType = arrayOf(
    shape({
        key: string.isRequired,
        label: string.isRequired,
        subCategories: arrayOf(
            shape({
                key: string.isRequired,
                label: string.isRequired,
            }).isRequired,
        ).isRequired,
    }),
)

export const debugSettingsType = shape({
    linesViz: bool.isRequired,
    lightsViz: bool.isRequired,
    depthViz: bool.isRequired,
    imitateLowPerformance: bool.isRequired,
    ssViz: bool.isRequired,
    nsViz: bool.isRequired,
    highlightPlanes: bool.isRequired,
    defaultToFloor: bool.isRequired,
    useFancyShadows: bool.isRequired,
    frontSideShadows: bool.isRequired,
    useMultiShadow: bool.isRequired,
    enableWalls: bool.isRequired,
    enableGPUframetimeStats: bool.isRequired,
    showMemoryStats: bool.isRequired,
    trackedMemoryType: number.isRequired,
    showProductAdminData: bool.isRequired,
    useTAAforScreenshots: bool.isRequired,
    hidePins: bool.isRequired,
})

export const previewSettingsType = shape({
    showOcclusions: bool.isRequired,
    showShadows: bool.isRequired,
    wallCollisions: bool.isRequired,
    showSurfaces: arrayOf(string).isRequired,
    magicEraserMode: bool.isRequired,
    canvasWidth: number,
})

export const ratingType = objectOf(
    shape({
        id: number,
        value: number,
        note: string,
        job: string.isRequired,
        user: number,
        ratingTag: number.isRequired,
    }),
)
export const ratingOptionsType = arrayOf(
    shape({
        id: number.isRequired,
        name: string.isRequired,
        kind: string.isRequired,
        position: number.isRequired,
    }).isRequired,
)

export const imageDataType = shape({})
export const textDataType = shape({})
export const historyType = shape({})
export const rendererType = shape({})
export const routerLocationType = shape({
    search: string.isRequired,
})

export const imageLoaderType = shape({ image: string.isRequired, subTitle: string })
export const imageLoaderListType = arrayOf(imageLoaderType)

export const trackingRoomInfoType = shape({
    RoomTitle: string,
    RoomId: number,
    RoomSource: string,
})

export const toastType = shape({
    toastType: string.isRequired,
    props: objectOf(oneOfType([string, number, bool])),
})

export const toastsType = arrayOf(toastType)

export const sidepanelTabsType = shape({
    isSwapSelected: bool.isRequired,
    isBrowseSelected: bool.isRequired,
    isMyListSelected: bool.isRequired,
    isFavoritesSelected: bool.isRequired,
    isDebugSelected: bool.isRequired,
    isRateSelected: bool.isRequired,
})
